import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { supabase } from "../lib/supabase";
import { COMMUNITY_INCLUDED } from "../lib/communityFeature";
import { SETTINGS_TITLE, accountTabs, type AccountPart } from "../lib/accountTabs";

/* JOB 3 STEP 5 PAGE 6 — THE SETTINGS HEAD: ONE head for the account's pages, the Library's grammar (`Workouts.tsx`: the
 * section's name as the h1, the page's one action beside it, the parts as the row under it). Each page drew an eyebrow
 * (`Account`, or `Clients` on Requests) over an h1 that repeated the tab marked under it — three lines saying where you are.
 * The h1 stays the focus target every arrival moves to (b4-7); `phoneTitle` keeps `#/page`'s phone name (`Profile`, the
 * app's tab) as the second of two exclusive spans. */
export function SettingsHead({ current, headingRef, action, phoneTitle }: {
  current: AccountPart;
  headingRef: RefObject<HTMLHeadingElement>;
  action?: ReactNode;
  phoneTitle?: string;
}) {
  return (
    <>
      <header className="page-head settings-head">
        <h1 className="ht-title" tabIndex={-1} ref={headingRef}>
          {phoneTitle === undefined ? SETTINGS_TITLE : (
            <>
              <span className="coachpage-title-wide">{SETTINGS_TITLE}</span>
              <span className="coachpage-title-phone">{phoneTitle}</span>
            </>
          )}
        </h1>
        {/* The hover row's brief (10-16) — LOG OUT IS THE HEAD'S, one quiet button right of the title, the same on every tab.
            It sat inside the `Next week` tab under `How you set next week`, as if it belonged to that setting. */}
        <button type="button" className="ht-button ht-button-quiet settings-signout"
                onClick={() => { void supabase.auth.signOut(); }}>
          Log Out
        </button>
        {action}
      </header>
      <AccountTabs current={current} />
    </>
  );
}

/* W72 step 8 — the account's five pages, one underline row under each page's title (`lib/accountTabs.ts`). It wears
 * the Library row's classes because it IS that control — one tab grammar on the web, not a second one drawn alike.
 *
 * ⚠️ AT PHONE WIDTH THE ROW SCROLLS SIDEWAYS, and five labels do not fit 375 px: RENDERED, `Your data` — the page you
 * are on — sat half off the right edge. So the current tab is brought into the row's view on arrival, by moving the
 * ROW's own scroll (never `scrollIntoView`, which would also scroll the page). */
export function AccountTabs({ current }: { current: AccountPart }) {
  const row = useRef<HTMLUListElement>(null);
  // W120: with the Community out, `Requests` is drawn only while one is pending — one count, asked once per row.
  const [pending, setPending] = useState(0);
  useEffect(() => {
    if (COMMUNITY_INCLUDED) return;
    let cancelled = false;
    void supabase.from("coach_requests").select("id", { count: "exact", head: true }).eq("status", "pending")
      .then(({ count }) => { if (!cancelled) setPending(count ?? 0); });
    return () => { cancelled = true; };
  }, []);
  const here = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    const ul = row.current, a = here.current;
    if (ul === null || a === null || ul.scrollWidth <= ul.clientWidth) return;
    const off = a.getBoundingClientRect().left - ul.getBoundingClientRect().left + ul.scrollLeft;
    ul.scrollLeft = Math.max(0, off - (ul.clientWidth - a.offsetWidth) / 2);
  }, [current]);
  return (
    <nav aria-label="Settings">
      <ul className="library-tabs account-tabs" ref={row}>
        {accountTabs(current, pending).map((t) => (
          <li key={t.part}>
            <a className="library-tab" href={t.href} ref={t.current ? here : undefined}
               aria-current={t.current ? "page" : undefined}>{t.label}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
